import React, { useEffect, useState, Suspense, lazy } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { Header, ActiveTab } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import type { EditorSource } from '@/features/editor/types';

// Code-split routes and major views for optimal performance (Phase 8 Performance Optimization)
const HomePage = lazy(() =>
  import('@/pages/HomePage').then((m) => ({ default: m.HomePage }))
);
const HealthPage = lazy(() =>
  import('@/pages/HealthPage').then((m) => ({ default: m.HealthPage }))
);
const DocsPage = lazy(() =>
  import('@/pages/DocsPage').then((m) => ({ default: m.DocsPage }))
);
const TemplateGallery = lazy(() =>
  import('@/features/templates').then((m) => ({ default: m.TemplateGallery }))
);
const MemeEditor = lazy(() =>
  import('@/features/editor').then((m) => ({ default: m.MemeEditor }))
);
const ProjectsView = lazy(() =>
  import('@/features/projects').then((m) => ({ default: m.ProjectsView }))
);
const CommunityFeed = lazy(() =>
  import('@/features/community').then((m) => ({ default: m.CommunityFeed }))
);
const ProfileView = lazy(() =>
  import('@/features/profile').then((m) => ({ default: m.ProfileView }))
);
const AuthScreen = lazy(() =>
  import('@/features/auth/components/AuthScreen').then((m) => ({ default: m.AuthScreen }))
);
const ModerationDashboard = lazy(() =>
  import('@/features/moderation').then((m) => ({ default: m.ModerationDashboard }))
);
const ContestsView = lazy(() =>
  import('@/features/contests').then((m) => ({ default: m.ContestsView }))
);
import { fetchModeratorStatus } from '@/features/moderation';
import { supabase } from '@/lib/supabase/client';
import type { NotificationResourceType } from '@/types/database.types';

export const App: React.FC = () => {
  const { session, loading } = useAuth();

  // Helper to read initial tab, memeId, and profile params from URL
  const getInitialUrlState = (): {
    tab: ActiveTab;
    memeId?: string;
    userId?: string;
    username?: string;
  } => {
    if (typeof window === 'undefined') return { tab: 'home' };

    const searchParams = new URLSearchParams(window.location.search);
    const tabParam = searchParams.get('tab') as ActiveTab | null;
    const memeParam = searchParams.get('meme');
    const userParam = searchParams.get('user');
    const usernameParam = searchParams.get('username');

    const validTabs: ActiveTab[] = [
      'home',
      'feed',
      'templates',
      'projects',
      'editor',
      'profile',
      'health',
      'docs',
      'auth',
      'moderation',
      'contests',
    ];

    if (memeParam) {
      return { tab: 'feed', memeId: memeParam };
    }

    if (tabParam === 'profile') {
      return {
        tab: 'profile',
        userId: userParam || undefined,
        username: usernameParam || undefined,
      };
    }

    if (tabParam && validTabs.includes(tabParam)) {
      return { tab: tabParam };
    }

    return { tab: 'home' };
  };

  const [initialUrlState] = useState(getInitialUrlState);
  const [activeTab, setActiveTab] = useState<ActiveTab>(initialUrlState.tab);
  const [authRedirectTab, setAuthRedirectTab] = useState<ActiveTab>('profile');
  const [selectedMemeId, setSelectedMemeId] = useState<string | undefined>(
    initialUrlState.memeId
  );
  const [profileUserId, setProfileUserId] = useState<string | undefined>(
    initialUrlState.userId
  );
  const [profileUsername, setProfileUsername] = useState<string | undefined>(
    initialUrlState.username
  );
  const [editorSource, setEditorSource] = useState<EditorSource | null>(null);
  const [editorProjectId, setEditorProjectId] = useState<string | undefined>(undefined);
  const [isModerator, setIsModerator] = useState<boolean>(false);

  // Fetch moderator status whenever user session changes
  useEffect(() => {
    let isMounted = true;
    if (!session?.user?.id) {
      setIsModerator(false);
      return;
    }
    fetchModeratorStatus(supabase, session.user.id)
      .then((status) => {
        if (isMounted) {
          setIsModerator(status.isModerator);
        }
      })
      .catch(() => {
        if (isMounted) {
          setIsModerator(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [session?.user?.id]);

  // Sync state to URL search parameters
  const updateUrl = (
    tab: ActiveTab,
    memeId?: string | null,
    profileUser?: { userId?: string | null; username?: string | null }
  ) => {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);
    if (
      tab === 'home' &&
      !memeId &&
      !profileUser?.userId &&
      !profileUser?.username
    ) {
      url.search = '';
    } else {
      url.searchParams.set('tab', tab);
      if (tab === 'feed' && memeId) {
        url.searchParams.set('meme', memeId);
      } else {
        url.searchParams.delete('meme');
      }

      if (tab === 'profile') {
        if (profileUser?.userId) {
          url.searchParams.set('user', profileUser.userId);
        } else {
          url.searchParams.delete('user');
        }
        if (profileUser?.username) {
          url.searchParams.set('username', profileUser.username);
        } else {
          url.searchParams.delete('username');
        }
      } else {
        url.searchParams.delete('user');
        url.searchParams.delete('username');
      }
    }
    window.history.pushState({}, '', url.toString());
  };

  const handleTabChange = (
    tab: ActiveTab,
    profileParams?: { userId?: string; username?: string }
  ) => {
    setActiveTab(tab);
    if (tab !== 'feed') {
      setSelectedMemeId(undefined);
    }
    if (tab === 'profile') {
      setProfileUserId(profileParams?.userId);
      setProfileUsername(profileParams?.username);
      updateUrl(tab, null, profileParams);
    } else {
      setProfileUserId(undefined);
      setProfileUsername(undefined);
      updateUrl(tab, tab === 'feed' ? selectedMemeId : null);
    }
  };

  const handleMemeSelect = (memeId: string | null) => {
    setSelectedMemeId(memeId || undefined);
    updateUrl('feed', memeId);
  };

  const handleRequireAuth = (targetTab: ActiveTab = 'profile') => {
    setAuthRedirectTab(targetTab);
    setActiveTab('auth');
    updateUrl('auth');
  };

  const handleAuthSuccess = () => {
    const target = authRedirectTab || 'profile';
    handleTabChange(target);
  };

  const handleNavigateNotificationResource = (
    resourceType: NotificationResourceType,
    resourceId: string
  ) => {
    if (resourceType === 'meme' || resourceType === 'comment') {
      setSelectedMemeId(resourceId);
      setActiveTab('feed');
      updateUrl('feed', resourceId);
    } else if (resourceType === 'profile') {
      handleTabChange('profile', { userId: resourceId });
    } else {
      handleTabChange('home');
    }
  };

  // Auto-redirect if user becomes authenticated while on auth tab
  useEffect(() => {
    if (session?.user && activeTab === 'auth') {
      const target = authRedirectTab || 'profile';
      handleTabChange(target);
    }
  }, [session, activeTab, authRedirectTab]);

  // Listen to browser popstate (back/forward button)
  useEffect(() => {
    const handlePopState = () => {
      const { tab, memeId, userId, username } = getInitialUrlState();
      setActiveTab(tab);
      setSelectedMemeId(memeId);
      setProfileUserId(userId);
      setProfileUsername(username);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Accessible loading state while auth initializes
  if (loading) {
    return <LoadingSpinner fullPage size="lg" label="Initializing Meme Mandi..." />;
  }

  const isAuthenticated = !!session;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Skip to main content link for keyboard & screen reader accessibility */}
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>

      <Header
        activeTab={activeTab}
        onTabChange={(t, params) => handleTabChange(t, params)}
        isAuthenticated={isAuthenticated}
        isModerator={isModerator}
        currentUserId={session?.user?.id || null}
        onSignIn={() => handleRequireAuth('profile')}
        onNavigateToNotificationResource={handleNavigateNotificationResource}
      />

      <main id="main-content" tabIndex={-1} style={{ flex: 1 }}>
        <ErrorBoundary resetKeys={[activeTab, selectedMemeId, profileUserId]}>
          <Suspense fallback={<LoadingSpinner fullPage size="lg" label="Loading view..." />}>
            {activeTab === 'home' && <HomePage onNavigate={(t) => handleTabChange(t as ActiveTab)} />}

            {activeTab === 'auth' && (
              <div style={{ padding: '2rem 1rem' }}>
                <AuthScreen onSuccess={handleAuthSuccess} />
              </div>
            )}

            {activeTab === 'feed' && (
              <CommunityFeed
                currentUserId={session?.user?.id}
                initialMemeId={selectedMemeId}
                onMemeSelect={handleMemeSelect}
                onRequireAuth={handleRequireAuth}
                onRemixMeme={(meme) => {
                  setEditorSource({
                    type: 'template',
                    templateId: meme.id,
                    templateName: `Remix of ${meme.creator.displayName}`,
                    imageUrl: meme.imageUrl,
                  });
                  setEditorProjectId(undefined);
                  handleTabChange('editor');
                }}
                onCreateMeme={() => {
                  if (!isAuthenticated) {
                    handleRequireAuth();
                  } else {
                    handleTabChange('templates');
                  }
                }}
              />
            )}

            {activeTab === 'profile' && (
              <ProfileView
                currentUserId={session?.user?.id}
                profileUserId={profileUserId}
                profileUsername={profileUsername}
                onOpenProject={(project) => {
                  setEditorSource({
                    type: 'project',
                    projectId: project.id,
                    title: project.title,
                    sourceImagePath: project.source_image_path || undefined,
                    dimensions: {
                      width: project.canvas_width,
                      height: project.canvas_height,
                    },
                  });
                  setEditorProjectId(project.id);
                  handleTabChange('editor');
                }}
                onMemeSelect={handleMemeSelect}
                onCreateMeme={() => handleTabChange('templates')}
                onRequireAuth={handleRequireAuth}
                onNavigateToTab={(t) => handleTabChange(t as ActiveTab)}
              />
            )}

            {activeTab === 'templates' && (
              <TemplateGallery
                onSelectTemplate={(template) => {
                  setEditorSource({
                    type: 'template',
                    templateId: template.id,
                    templateName: template.name,
                    imageUrl: template.imageUrl,
                  });
                  setEditorProjectId(undefined);
                  handleTabChange('editor');
                }}
                onUploadComplete={(file, result) => {
                  setEditorSource({
                    type: 'upload',
                    projectId: result?.projectId || `proj-${Date.now()}`,
                    storagePath: result?.path || '',
                    file,
                    dimensions: result
                      ? { width: result.width, height: result.height }
                      : undefined,
                  });
                  setEditorProjectId(result?.projectId);
                  handleTabChange('editor');
                }}
              />
            )}

            {activeTab === 'projects' && (
              isAuthenticated ? (
                <ProjectsView
                  onOpenProject={(project) => {
                    setEditorSource({
                      type: 'project',
                      projectId: project.id,
                      title: project.title,
                      sourceImagePath: project.source_image_path || undefined,
                      dimensions: {
                        width: project.canvas_width,
                        height: project.canvas_height,
                      },
                    });
                    setEditorProjectId(project.id);
                    handleTabChange('editor');
                  }}
                  onCreateMeme={() => handleTabChange('templates')}
                />
              ) : (
                <div style={{ padding: '2rem 1rem' }}>
                  <AuthScreen />
                </div>
              )
            )}

            {activeTab === 'editor' && (
              <MemeEditor
                source={editorSource}
                projectId={editorProjectId}
                onBack={() => handleTabChange(isAuthenticated ? 'projects' : 'templates')}
                onSourceChange={setEditorSource}
              />
            )}

            {activeTab === 'health' && <HealthPage />}
            {activeTab === 'docs' && <DocsPage />}
            {activeTab === 'moderation' && (
              <ModerationDashboard
                currentUserId={session?.user?.id || null}
                onNavigateHome={() => handleTabChange('home')}
              />
            )}
            {activeTab === 'contests' && (
              <ContestsView
                currentUserId={session?.user?.id || null}
                onRequireAuth={() => handleRequireAuth('contests')}
                onMemeSelect={handleMemeSelect}
                onCreateMeme={() => handleTabChange('templates')}
              />
            )}
          </Suspense>
        </ErrorBoundary>
      </main>
      <Footer />
    </div>
  );
};

export default App;
